import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  FormGroup,
  Label,
  Input,
  Button,
  Spinner,
  Form,
  Badge,
} from "reactstrap";
import { useFormik } from "formik";
import * as Yup from "yup";
import Select from "react-select";
import Dropzone from "react-dropzone";
import { Link } from "react-router-dom";
import toast from "react-hot-toast";
import BreadCrumb from "../../../Components/Common/BreadCrumb";
import CategoryServices from "../../../services/OrderingServices/CategoryService";
import CostCenterService from "../../../services/OrderingServices/CostCenterService";

const CategoryCreate = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isCanceling, setIsCanceling] = useState(false);
  const [categoryOptions, setCategoryOptions] = useState([]);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [existingFiles, setExistingFiles] = useState([]);
  const [shouldRemoveExistingFile, setShouldRemoveExistingFile] =
    useState(false);
  const [costCenterOptions, setCostCenterOptions] = useState([]);

  // Validation Schema
  const validationSchema = Yup.object().shape({
    name: Yup.string().required("Name is required"),
    // description: Yup.string().required("Description is required"),
    parentId: Yup.string().nullable(),

    //
    costCenterId: Yup.string().required("Cost Center is required"),
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await CategoryServices.maincategoryList();
        setCategoryOptions(response?.items);
      } catch (error) {
        console.error("Error fetching data:", error);
        toast.error("Failed to fetch categories");
      }
    };

    fetchData();
  }, []);
  useEffect(() => {
    const CostCenterData = async () => {
      try {
        const response = await CostCenterService.getList();
        setCostCenterOptions(response);
      } catch (error) {
        console.error("Error fetching data:", error);
        toast.error("Failed to fetch categories");
      }
    };

    CostCenterData();
  }, []);
  const handleAcceptedFiles = (files) => {
    if (files && files.length > 0) {
      const formattedFiles = files.map((file) => ({
        name: file.name,
        preview: URL.createObjectURL(file),
      }));
      setSelectedFiles(formattedFiles);
      formik.setFieldValue("formfile", files[0]);
    }
  };

  const handleFileRemove = (index, isExisting = false) => {
    if (isExisting) {
      setExistingFiles([]);
      setShouldRemoveExistingFile(true);
      formik.setFieldValue("formfile", null);
    } else {
      const files = [...selectedFiles];
      files.splice(index, 1);
      setSelectedFiles(files);
      formik.setFieldValue("formfile", null);
    }
  };

  useEffect(() => {
    const fetchCategoryDetail = async () => {
      try {
        const response = await CategoryServices.viewcategory(id);
        console.log("🚀 ~ fetchCategoryDetail ~ response:", response);

        if (response.imageUrl) {
          const formattedFiles = [
            {
              name: response.imageUrl.split("/").pop(),
              preview:
                response.displayImage?.getCDNUrl() ||
                `${window.APP_CONFIG.api1.baseapiV2}/${response.imageUrl}`,
            },
          ];
          setExistingFiles(formattedFiles);
        }

        formik.setValues({
          id: response?.id,
          name: response.name || "",
          parentId: response.parentId,
          description: response.description || "",
          formfile: null,
          isActive: response.isActive,
          isHidden: response.isHidden,
          costCenterId: response?.costCenterId,
          categoryPosition: response?.categoryPosition,
        });
      } catch (error) {
        console.error("Error fetching category detail:", error);
        toast.error("Failed to fetch category details");
      }
    };

    if (id) {
      fetchCategoryDetail();
      setIsEditing(true);
    }
  }, [id]);

  const formik = useFormik({
    initialValues: {
      id: "",
      name: "",
      description: "",
      parentId: null,
      formfile: null,
      isActive: true,
      isHidden: false,
      costCenterId: null,
      categoryPosition: 0,
    },

    validationSchema,
    onSubmit: async (values) => {
      try {
        setIsSaving(true);

        const payload = {
          id: values.id,
          name: values.name,
          description: values.description,
          isHidden: values.isHidden,
          categoryPosition: Number(values.categoryPosition) || 0,
          costCenterId: Number(values.costCenterId) || 0,
          isActive: values.isActive,
        };

        // Conditionally add parentId only if it's valid
        if (values.parentId && Number(values.parentId) !== 0) {
          payload.parentId = Number(values.parentId);
        }

        // Optionally include image info if needed
        payload.imageName = "";
        payload.imagePath = "";

        if (isEditing) {
          await CategoryServices.categoryUpdate(id, payload);
          toast.success("Category Updated Successfully");
        } else {
          await CategoryServices.categorycreate(payload);
          toast.success("Category Added Successfully");
        }

        navigate("/category-list");
      } catch (error) {
        console.error("Error:", error);
        toast.error("Error saving category");
      } finally {
        setIsSaving(false);
      }
    },
  });

  console.log("🚀 ~ formik.values:", formik.values);

  const breadcrumbItems = [{ title: "Back to List", link: "/category-list" }];

  return (
    <Container fluid>
      <div className="page-content">
        <BreadCrumb
          title="Category Form"
          pageTitle="Category"
          breadcrumbItems={breadcrumbItems}
        />
        <Row>
          <Col>
            <Card>
              <CardBody>
                <Form onSubmit={formik.handleSubmit}>
                  <Row className="">
                    <Col lg={6} md={12} className="mb-2">
                      <Label for="name">
                        Name: <span className="text-danger">*</span>
                      </Label>
                      <Input
                        type="text"
                        id="name"
                        name="name"
                        placeholder="Enter category name"
                        value={formik.values.name}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        invalid={formik.touched.name && formik.errors.name}
                      />
                      {formik.touched.name && formik.errors.name && (
                        <div className="text-danger">{formik.errors.name}</div>
                      )}
                    </Col>
                    <Col lg={6} md={12} className="mb-2">
                      <Label htmlFor="parentId" className="form-label">
                        Parent Category:
                      </Label>
                      <Select
                        name="parentId"
                        id="parentId"
                        placeholder="Select Parent Category"
                        value={
                          categoryOptions
                            .map((option) => ({
                              value: option.id,
                              label: option.name,
                            }))
                            .find(
                              (option) =>
                                option.value === formik.values.parentId
                            ) || null
                        }
                        onChange={(selectedOption) => {
                          formik.setFieldValue(
                            "parentId",
                            selectedOption ? selectedOption.value : null
                          );
                        }}
                        options={categoryOptions.map((option) => ({
                          value: option.id,
                          label: option.name,
                        }))}
                        isClearable
                      />
                    </Col>

                    <Col lg={6} md={12} className="mb-2">
                      <Label for="description">
                        {/* <span className="text-danger">*</span> */}
                        Description:
                      </Label>
                      <Input
                        type="textarea"
                        id="description"
                        name="description"
                        style={{ height: "150px" }}
                        placeholder="Enter description"
                        value={formik.values.description}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        invalid={
                          formik.touched.description &&
                          formik.errors.description
                        }
                      />
                      {formik.touched.description &&
                        formik.errors.description && (
                          <div className="text-danger">
                            {formik.errors.description}
                          </div>
                        )}
                    </Col>

                    <Col lg={6} md={12} className="mb-2">
                      <Row>
                        <Col lg={6} md={12} className="mb-2">
                          <Label for="categoryPosition">
                            Category Position:
                          </Label>
                          <Input
                            type="number"
                            id="categoryPosition"
                            name="categoryPosition"
                            placeholder="Enter category position"
                            value={formik.values.categoryPosition}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            invalid={
                              formik.touched.categoryPosition &&
                              formik.errors.categoryPosition
                            }
                          />
                          {formik.touched.categoryPosition &&
                            formik.errors.categoryPosition && (
                              <div className="text-danger">
                                {formik.errors.categoryPosition}
                              </div>
                            )}
                        </Col>
                        <Col lg={6} md={12} className="mb-2">
                          <Label htmlFor="costCenterId" className="form-label">
                            Cost Center: <span className="text-danger">*</span>
                          </Label>
                          <Select
                            name="costCenterId"
                            id="costCenterId"
                            placeholder="Select Cost Center"
                            value={
                              costCenterOptions
                                .map((option) => ({
                                  value: option.id,
                                  label: option.printerName,
                                }))
                                .find(
                                  (option) =>
                                    option.value === formik.values.costCenterId
                                ) || null
                            }
                            onChange={(selectedOption) => {
                              formik.setFieldValue(
                                "costCenterId",
                                selectedOption ? selectedOption.value : null
                              );
                            }}
                            options={costCenterOptions.map((option) => ({
                              value: option.id,
                              label: option.printerName,
                            }))}
                            isClearable
                            onBlur={() =>
                              formik.setFieldTouched("costCenterId", true)
                            }
                          />
                          {formik.touched.costCenterId &&
                            formik.errors.costCenterId && (
                              <div className="text-danger">
                                {formik.errors.costCenterId}
                              </div>
                            )}
                        </Col>
                        <Col lg={6} md={12} className="mb-2">
                          <Label>Active Status:</Label>
                          <div className="form-check mt-2">
                            <Input
                              type="checkbox"
                              id="isActive"
                              name="isActive"
                              checked={formik.values.isActive}
                              onChange={formik.handleChange}
                              className="form-check-input"
                            />
                            <Label
                              check
                              htmlFor="isActive"
                              className="form-check-label"
                            >
                              Is Active
                            </Label>
                          </div>
                        </Col>

                        <Col lg={6} md={12} className="mb-2">
                          <Label>Hide Status:</Label>
                          <FormGroup
                            check
                            switch
                            className="d-flex align-items-center mt-2"
                          >
                            <Input
                              type="switch"
                              role="switch"
                              name="isHidden"
                              checked={formik.values.isHidden}
                              onChange={(e) =>
                                formik.setFieldValue(
                                  "isHidden",
                                  e.target.checked
                                )
                              }
                            />
                            <Label check className="ms-2">
                              <Badge
                                color={
                                  formik.values.isHidden ? "success" : "danger"
                                }
                              >
                                {formik.values.isHidden
                                  ? "Enabled"
                                  : "Disabled"}
                              </Badge>
                            </Label>
                          </FormGroup>
                        </Col>
                      </Row>
                    </Col>
                  </Row>

                  <div className="text-end mt-4">
                    <Button
                      type="submit"
                      className="me-2"
                      color="success"
                      disabled={isSaving}
                    >
                      {isSaving ? (
                        <>
                          <Spinner size="sm" className="me-2" />
                          Saving...
                        </>
                      ) : (
                        "Save"
                      )}
                    </Button>
                    <Button
                      color="danger"
                      onClick={() => {
                        setIsCanceling(true);
                        navigate("/category-list");
                      }}
                      disabled={isCanceling || isSaving}
                    >
                      {isCanceling ? (
                        <>
                          <Spinner size="sm" className="me-2" />
                          Canceling...
                        </>
                      ) : (
                        "Cancel"
                      )}
                    </Button>
                  </div>
                </Form>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </div>
    </Container>
  );
};

export default CategoryCreate;
